import { api } from './api';
import type { MailboxFolder } from './mailboxFolders';
import type { IntakeRequest, LogEnquiryPayload } from './intakeApi';

export type { MailboxFolder };
export type InboxFolder = MailboxFolder;

export interface EmailSummary {
  id: string;
  mailboxId: string;
  direction: 'inbound' | 'outbound';
  fromAddress: string;
  toAddresses: string[];
  subject: string;
  snippet: string | null;
  isRead: boolean;
  isStarred: boolean;
  isArchived: boolean;
  isTrashed: boolean;
  isDraft: boolean;
  isSpam: boolean;
  hasReplyFlag: boolean;
  labels: string[];
  providerThreadId: string | null;
  linkStatus: 'unlinked' | 'suggested' | 'confirmed' | 'rejected';
  linkConfidence: number | null;
  matterId: string | null;
  matter: { id: string; caseNumber: string; caseName: string } | null;
  attachmentCount: number;
  threadCount: number;
  // Outbound mirror state to Gmail/Outlook: null = in sync; 'pending' = syncing;
  // 'failed' = could not reach provider after retries.
  syncStatus?: 'pending' | 'failed' | null;
  sentAt: string | null;
  receivedAt: string | null;
}

export interface ThreadEmail {
  id: string;
  fromAddress: string;
  toAddresses: string[];
  ccAddresses: string[];
  subject: string;
  snippet: string | null;
  bodyText: string | null;
  bodyHtml: string | null;
  bodyFetched: boolean;
  isRead: boolean;
  isStarred: boolean;
  direction: 'inbound' | 'outbound';
  receivedAt: string | null;
  sentAt: string | null;
  attachments: { id: string; filename: string; mimeType: string; sizeBytes: number | null }[];
}

export interface EmailDetail extends EmailSummary {
  bodyText: string | null;
  bodyHtml: string | null;
  bodyFetched: boolean;
  ccAddresses: string[];
  providerThreadId: string | null;
  attachments: {
    id: string;
    filename: string;
    mimeType: string;
    sizeBytes: number | null;
  }[];
  proposals: {
    id: string;
    type: string;
    status: string;
    title: string;
    detail: string;
  }[];
}

export interface EmailListResult {
  emails: EmailSummary[];
  nextCursor: string | null;
  total: number;
}

export async function fetchEmails(opts: {
  folder?: MailboxFolder;
  mailboxId?: string;
  cursor?: string;
  limit?: number;
}): Promise<EmailListResult> {
  const params = new URLSearchParams();
  if (opts.folder) params.set('folder', opts.folder);
  if (opts.mailboxId) params.set('mailboxId', opts.mailboxId);
  if (opts.cursor) params.set('cursor', opts.cursor);
  if (opts.limit) params.set('limit', String(opts.limit));
  const res = await api.get(`/inbox/emails?${params}`);
  return res.data;
}

export async function fetchEmail(emailId: string): Promise<EmailDetail> {
  const res = await api.get(`/inbox/emails/${emailId}`);
  return res.data;
}

export async function markRead(emailId: string, isRead: boolean): Promise<void> {
  await api.post(`/inbox/emails/${emailId}/mark-read`, { isRead });
}

export async function archiveEmail(emailId: string): Promise<void> {
  await api.post(`/inbox/emails/${emailId}/archive`);
}

export async function trashEmail(emailId: string): Promise<void> {
  await api.post(`/inbox/emails/${emailId}/trash`);
}

export async function toggleFollowUp(emailId: string): Promise<boolean> {
  const res = await api.post(`/inbox/emails/${emailId}/follow-up`);
  return res.data.followUp;
}

export async function toggleAwaitingResponse(emailId: string): Promise<boolean> {
  const res = await api.post(`/inbox/emails/${emailId}/awaiting-response`);
  return res.data.awaitingResponse;
}

export async function flagBillingOpportunity(emailId: string): Promise<boolean> {
  const res = await api.post(`/inbox/emails/${emailId}/billing-opportunity`);
  return res.data.billingOpportunity;
}

export async function linkEmailToMatter(emailId: string, matterId: string): Promise<void> {
  await api.post(`/inbox/emails/${emailId}/link`, { matterId });
}

export async function unlinkEmailFromMatter(emailId: string): Promise<void> {
  await api.delete(`/inbox/emails/${emailId}/link`);
}

// Links the whole thread (used by the conversation view and "Accept suggestion").
/**
 * Classify a conversation as NEW WORK and raise one pre-matter enquiry from it.
 *
 * The pre-matter sibling of linkConversationToMatter. Requires an explicit human action —
 * nothing on the mail sync path creates an enquiry. Idempotent: if the conversation already
 * carries an enquiry the server returns that one instead of raising a second.
 */
export async function startEnquiryFromConversation(
  conversationId: string,
  // The same payload the "Log an enquiry" form sends — one enquiry contract, whichever door
  // the work came through. arrivalChannel is overridden server-side: it came by email.
  payload: LogEnquiryPayload,
): Promise<IntakeRequest> {
  // Returns the enquiry itself, not just an id — the caller must be able to surface the
  // conflict verdict found on arrival, which is the whole reason the scan runs here.
  const res = await api.post(`/inbox/conversations/${conversationId}/start-enquiry`, payload);
  return res.data?.data ?? res.data;
}

export async function linkConversationToMatter(conversationId: string, matterId: string): Promise<void> {
  await api.post(`/inbox/conversations/${conversationId}/link`, { matterId });
}

// Dismisses a suggested matter link on a thread (marks it rejected so it won't re-suggest).
export async function dismissConversationSuggestion(conversationId: string): Promise<void> {
  await api.post(`/inbox/conversations/${conversationId}/dismiss-suggestion`);
}

export async function toggleStar(emailId: string): Promise<boolean> {
  const res = await api.post<{ starred: boolean }>(`/inbox/emails/${emailId}/star`);
  return res.data.starred;
}

// Stars/unstars the whole thread (conversation rows carry a conversationId, not an
// emailId — they must hit this endpoint, not toggleStar).
export async function toggleConversationStar(conversationId: string): Promise<boolean> {
  const res = await api.post<{ isStarred: boolean }>(`/inbox/conversations/${conversationId}/star`);
  return res.data.isStarred;
}

export async function toggleSpam(emailId: string): Promise<boolean> {
  const res = await api.post<{ spam: boolean }>(`/inbox/emails/${emailId}/spam`);
  return res.data.spam;
}

export async function bulkArchive(ids: string[]): Promise<void> {
  await api.post('/inbox/emails/bulk/archive', { ids });
}

export async function bulkMarkRead(ids: string[], isRead: boolean): Promise<void> {
  await api.post('/inbox/emails/bulk/mark-read', { ids, isRead });
}

export async function bulkDelete(ids: string[]): Promise<void> {
  await api.post('/inbox/emails/bulk/delete', { ids });
}

export async function getThreadMessages(providerThreadId: string): Promise<ThreadEmail[]> {
  const res = await api.get<{ messages: ThreadEmail[] }>(`/inbox/threads/${providerThreadId}/messages`);
  return res.data.messages;
}

// ─── Conversations (thread-first inbox) ──────────────────────

export interface ConversationSummary {
  id: string;
  mailboxId: string;
  providerThreadId: string;
  subject: string;
  participants: string[];
  lastMessageAt: string;
  lastSnippet: string | null;
  lastFromAddress: string;
  unreadCount: number;
  totalCount: number;
  hasAttachments: boolean;
  isInbox: boolean;
  isStarred: boolean;
  isTrashed: boolean;
  isSpam: boolean;
  isArchived: boolean;
  matterId: string | null;
  linkStatus: 'unlinked' | 'suggested' | 'confirmed' | 'rejected';
  linkConfidence: number | null;
  hasHearingRef: boolean;
  hasDeadline: boolean;
  isWithoutPrej: boolean;
  isBillingRelevant: boolean;
  awaitingReply: boolean;
  awaitingReplySince: string | null;
  nextDeadlineAt: string | null;
  matter: { id: string; caseNumber: string; caseName: string } | null;
}

export interface ConversationDetail extends ConversationSummary {
  emails: {
    id: string;
    fromAddress: string;
    toAddresses: string[];
    ccAddresses: string[];
    subject: string;
    snippet: string | null;
    bodyText: string | null;
    bodyHtml: string | null;
    bodyFetched: boolean;
    direction: 'inbound' | 'outbound';
    isRead: boolean;
    isStarred: boolean;
    isTrashed: boolean;
    isSpam: boolean;
    hasReplyFlag: boolean;
    labels: string[];
    receivedAt: string | null;
    sentAt: string | null;
    attachments: { id: string; filename: string; mimeType: string; sizeBytes: number | null }[];
  }[];
}

export interface ConversationListResult {
  conversations: ConversationSummary[];
  nextCursor: string | null;
  total: number;
}

export interface SyncJob {
  id: string;
  mailboxId: string;
  emailAddress: string | undefined;
  provider: string | undefined;
  jobType: string;
  status: string;
  totalEstimated: number | null;
  totalProcessed: number;
  error: string | null;
  startedAt: string | null;
  createdAt: string;
}

export async function fetchConversations(opts: {
  folder?: MailboxFolder;
  mailboxId?: string;
  cursor?: string;
  limit?: number;
}): Promise<ConversationListResult> {
  const params = new URLSearchParams();
  if (opts.folder)    params.set('folder', opts.folder);
  if (opts.mailboxId) params.set('mailboxId', opts.mailboxId);
  if (opts.cursor)    params.set('cursor', opts.cursor);
  if (opts.limit)     params.set('limit', String(opts.limit));
  const res = await api.get(`/inbox/conversations?${params}`);
  return res.data;
}

export async function fetchConversation(conversationId: string): Promise<ConversationDetail> {
  const res = await api.get(`/inbox/conversations/${conversationId}`);
  return res.data.conversation;
}

// ─── Communication Context (assembled read model) ──────────────
// Mirrors lib/communication-context/types.ts on the API. The panel RENDERS this
// and computes nothing — every item already carries its source + whether it is
// an authoritative fact.

export type ContextSource =
  | 'Matter' | 'MoneyFacts' | 'MatterActivity' | 'Hearings'
  | 'StrategicAwareness' | 'Relationship' | 'LegalSignals' | 'Conversation';

export interface ContextItem {
  title: string;
  value: string;
  source: ContextSource;
  authoritative: boolean;
  confidence?: 'High' | 'Medium' | 'Low';
  evidence?: string[];
  lastUpdatedAt: string | null;
  trace?: { entity: string; id: string };
}

export interface CommunicationMatterHeader {
  id: string;
  caseNumber: string;
  caseName: string;
  status: string;
  court: string | null;
  client: string | null;
  solicitor: string | null;
  opponent: string | null;
}

export interface AttentionItem {
  label: string;
  reason: string;
  contributors: ContextSource[];
}

export interface CommunicationContext {
  conversationId: string;
  sections: {
    matter: CommunicationMatterHeader | null;
    facts: {
      money: ContextItem[];
      activity: ContextItem[];
      hearing: ContextItem[];
      conversation: ContextItem[];
    };
    signals: ContextItem[];
    relationships: {
      instructingSolicitor: ContextItem[];
      layClient: ContextItem[];
    };
    attention: AttentionItem[];
  };
  decisionSupport: never[];
}

export async function fetchConversationContext(conversationId: string): Promise<CommunicationContext> {
  const res = await api.get(`/inbox/conversations/${conversationId}/context`);
  return res.data.context;
}

// Best-effort usage telemetry for the context panel (fire-and-forget — never
// blocks or errors the UI). Log-based on the server; evidence for later, not now.
export function trackContextEvent(
  event: 'panel_opened' | 'trace_clicked' | 'link_matter_clicked',
  meta?: { source?: string; linked?: boolean },
): void {
  void api.post('/inbox/context/telemetry', { event, ...meta }).catch(() => { /* ignore */ });
}

export async function fetchSyncStatus(): Promise<SyncJob[]> {
  const res = await api.get('/inbox/sync-status');
  return res.data.jobs;
}

// ─── Send path ────────────────────────────────────────────────

export interface ComposeEmailInput {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  bodyHtml?: string;
  bodyText?: string;
  matterId?: string;
  scheduledAt?: string;
  attachments?: File[];
}

export interface ReplyEmailInput {
  bodyHtml?: string;
  bodyText?: string;
  replyAll?: boolean;
  attachments?: File[];
}

export interface ForwardEmailInput {
  to: string[];
  cc?: string[];
  bodyHtml?: string;
  bodyText?: string;
  attachments?: File[];
}

// Build multipart/form-data for sends that carry files. List fields are
// JSON-encoded so the API can reconstruct arrays; scalars go as-is; each
// File is appended under "attachments" (matching multer's upload.array).
function buildSendFormData(fields: Record<string, unknown>, files: File[]): FormData {
  const fd = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined || value === null) continue;
    fd.append(key, Array.isArray(value) ? JSON.stringify(value) : String(value));
  }
  for (const file of files) fd.append('attachments', file, file.name);
  return fd;
}

export async function composeEmail(input: ComposeEmailInput): Promise<EmailDetail> {
  const { attachments, ...fields } = input;
  const payload = attachments?.length ? buildSendFormData(fields, attachments) : fields;
  const res = await api.post('/inbox/emails/send', payload);
  return res.data;
}

export async function replyToEmail(emailId: string, input: ReplyEmailInput): Promise<EmailDetail> {
  const { attachments, ...fields } = input;
  const payload = attachments?.length ? buildSendFormData(fields, attachments) : fields;
  const res = await api.post(`/inbox/emails/${emailId}/reply`, payload);
  return res.data;
}

export async function forwardEmail(emailId: string, input: ForwardEmailInput): Promise<EmailDetail> {
  const { attachments, ...fields } = input;
  const payload = attachments?.length ? buildSendFormData(fields, attachments) : fields;
  const res = await api.post(`/inbox/emails/${emailId}/forward`, payload);
  return res.data;
}

export async function cancelScheduledEmail(emailId: string): Promise<void> {
  await api.delete(`/inbox/emails/${emailId}/scheduled`);
}

// ─── Drafts (autosave) ────────────────────────────────────────

export interface DraftInput {
  to?: string[];
  cc?: string[];
  bcc?: string[];
  subject?: string;
  bodyHtml?: string;
  bodyText?: string;
  matterId?: string;
}

// Create a new draft (POST) or update an existing one (PUT). Returns the draft
// row including its id, which the composer holds onto for subsequent autosaves.
export async function saveDraft(input: DraftInput, draftId?: string): Promise<EmailDetail> {
  const res = draftId
    ? await api.put(`/inbox/drafts/${draftId}`, input)
    : await api.post('/inbox/drafts', input);
  return res.data;
}

export async function deleteDraft(draftId: string): Promise<void> {
  await api.delete(`/inbox/drafts/${draftId}`);
}

// Save an email attachment straight into a matter's document store.
// Returns the created Document record.
export async function saveAttachmentToMatter(
  emailId: string,
  attachmentId: string,
  matterId: string,
): Promise<{ id: string; name: string }> {
  const res = await api.post(
    `/inbox/emails/${emailId}/attachments/${attachmentId}/save-to-matter`,
    { matterId },
  );
  return res.data;
}

export async function downloadAttachment(emailId: string, attachmentId: string, filename: string): Promise<void> {
  const res = await api.get(`/inbox/emails/${emailId}/attachments/${attachmentId}/download`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(url); document.body.removeChild(a); }, 100);
}

// ─── Search ──────────────────────────────────────────────────

export interface EmailSearchFilters {
  q?: string;
  from?: string;
  to?: string;
  after?: string;
  before?: string;
  matterId?: string;
  labelId?: string;
  hasAttachment?: boolean;
  isRead?: boolean;
  mailboxId?: string;
  folder?: string;
  cursor?: string;
  limit?: number;
}

export interface EmailSearchResult {
  id: string;
  mailboxId: string;
  direction: string;
  fromAddress: string;
  toAddresses: string[];
  subject: string;
  snippet: string | null;
  isRead: boolean;
  isStarred: boolean;
  hasAttachment: boolean;
  linkStatus: string;
  matterId: string | null;
  matter: { id: string; caseNumber: string; caseName: string } | null;
  receivedAt: string | null;
  sentAt: string | null;
}

export interface EmailSearchResponse {
  results: EmailSearchResult[];
  total: number;
  nextCursor: string | null;
}

export async function searchEmails(filters: EmailSearchFilters): Promise<EmailSearchResponse> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== '') {
      params.set(k, String(v));
    }
  }
  const res = await api.get(`/inbox/search?${params.toString()}`);
  return res.data;
}
